import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Building2, 
  Plus, 
  Edit, 
  Trash2, 
  Users, 
  GraduationCap, 
  Loader2, 
  Mail, 
  X,
  Shield,
  Globe,
  CreditCard,
  Calendar,
  Clock,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  Package,
  Settings,
  ChevronDown
} from 'lucide-react';

// Account type configuration
const TYPE_CONFIG = {
  pilot: { label: 'Pilot', color: 'bg-blue-100 text-blue-700', description: 'Full access, no billing' },
  trial: { label: 'Trial', color: 'bg-amber-100 text-amber-700', description: 'Trial period with countdown' },
  free: { label: 'Free', color: 'bg-slate-100 text-slate-600', description: 'Free tier with limits' },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700', description: 'Active subscription' }
};

// Account status configuration
const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-green-100 text-green-700', icon: CheckCircle2, description: 'Full access' },
  inactive: { label: 'Inactive', color: 'bg-slate-100 text-slate-600', icon: X, description: 'Admin disabled' },
  past_due: { label: 'Past Due', color: 'bg-red-100 text-red-700', icon: AlertTriangle, description: 'Payment issue' },
  canceled: { label: 'Canceled', color: 'bg-slate-100 text-slate-600', icon: X, description: 'Subscription ended' },
  expired: { label: 'Expired', color: 'bg-red-100 text-red-700', icon: AlertCircle, description: 'Access restricted' }
};

const PLAN_CONFIG = {
  core: { label: 'Core', description: 'Full platform access' },
  premium: { label: 'Premium', description: 'Core + AI features' }
};

const PRODUCTS = [
  { id: 'interventions', label: 'Interventions', description: 'Core MTSS tracking' },
  { id: 'assessments', label: 'Assessments', description: 'Coming soon' }
];

export default function SchoolsPage() {
  const { isSuperAdmin, user } = useAuth();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [editingSchool, setEditingSchool] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    code: '',
    approved_email_domains: []
  });
  const [accountData, setAccountData] = useState({
    account_type: 'pilot',
    account_status: 'active',
    plan: null,
    trial_started_at: null,
    trial_ends_at: null,
    notes: '',
    products_enabled: ['interventions'],
    extend_trial_days: null,
    extension_reason: ''
  });
  const [newDomain, setNewDomain] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSchools();
  }, []);

  const loadSchools = async () => {
    try {
      const response = await apiService.getSchools();
      setSchools(response.data);
    } catch (error) {
      toast.error('Failed to load schools');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('School name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingSchool) {
        await apiService.updateSchool(editingSchool.id, formData);
        toast.success('School updated successfully');
      } else {
        await apiService.createSchool(formData);
        toast.success('School created successfully');
      }
      setShowAddModal(false);
      setEditingSchool(null);
      setFormData({ name: '', code: '', approved_email_domains: [] });
      setNewDomain('');
      loadSchools();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save school');
    } finally {
      setSaving(false);
    }
  };

  const handleAccountSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSchool) return;

    setSaving(true);
    try {
      const updateData = {
        account_type: accountData.account_type,
        account_status: accountData.account_status,
        plan: accountData.plan,
        notes: accountData.notes,
        products_enabled: accountData.products_enabled
      };

      // Add trial dates if explicitly set
      if (accountData.trial_started_at) {
        updateData.trial_started_at = accountData.trial_started_at;
      }
      if (accountData.trial_ends_at) {
        updateData.trial_ends_at = accountData.trial_ends_at;
      }

      // Add extension if specified
      if (accountData.extend_trial_days && accountData.extend_trial_days > 0) {
        updateData.extend_trial_days = parseInt(accountData.extend_trial_days);
        updateData.extension_reason = accountData.extension_reason;
      }

      await apiService.updateSchoolAccount(selectedSchool.id, updateData);
      toast.success('Account updated successfully');
      setShowSubscriptionModal(false);
      setSelectedSchool(null);
      loadSchools();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update account');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (school) => {
    setEditingSchool(school);
    setFormData({ 
      name: school.name, 
      code: school.code || '',
      approved_email_domains: school.approved_email_domains || []
    });
    setShowAddModal(true);
  };

  const handleManageSubscription = (school) => {
    setSelectedSchool(school);
    setAccountData({
      account_type: school.account_type || 'pilot',
      account_status: school.account_status || 'active',
      plan: school.plan || null,
      trial_started_at: school.trial_started_at || null,
      trial_ends_at: school.trial_ends_at || null,
      notes: school.account_notes || '',
      products_enabled: school.products_enabled || ['interventions'],
      extend_trial_days: null,
      extension_reason: ''
    });
    setShowSubscriptionModal(true);
  };

  const handleDelete = async (school) => {
    if (!window.confirm(`Are you sure you want to deactivate "${school.name}"? This will not delete student data.`)) {
      return;
    }

    try {
      await apiService.deleteSchool(school.id);
      toast.success('School deactivated');
      loadSchools();
    } catch (error) {
      toast.error('Failed to deactivate school');
    }
  };

  const addDomain = () => {
    const domain = newDomain.trim().toLowerCase();
    if (!domain) return;
    
    if (!domain.includes('.')) {
      toast.error('Please enter a valid domain (e.g., school.edu)');
      return;
    }
    
    const cleanDomain = domain.replace('@', '');
    
    if (formData.approved_email_domains.includes(cleanDomain)) {
      toast.error('Domain already added');
      return;
    }
    
    setFormData({
      ...formData,
      approved_email_domains: [...formData.approved_email_domains, cleanDomain]
    });
    setNewDomain('');
  };

  const removeDomain = (domain) => {
    setFormData({
      ...formData,
      approved_email_domains: formData.approved_email_domains.filter(d => d !== domain)
    });
  };

  const toggleProduct = (productId) => {
    const current = accountData.products_enabled || [];
    if (current.includes(productId)) {
      setAccountData({
        ...accountData,
        products_enabled: current.filter(p => p !== productId)
      });
    } else {
      setAccountData({
        ...accountData,
        products_enabled: [...current, productId]
      });
    }
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingSchool(null);
    setFormData({ name: '', code: '', approved_email_domains: [] });
    setNewDomain('');
  };

  const closeSubscriptionModal = () => {
    setShowSubscriptionModal(false);
    setSelectedSchool(null);
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return '-';
    try {
      return new Date(isoDate).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch {
      return '-';
    }
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.active;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon size={12} />
        {config.label}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const config = TYPE_CONFIG[type] || TYPE_CONFIG.pilot;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  if (!isSuperAdmin) {
    return (
      <div className="p-8 text-center">
        <h1 className="text-2xl font-bold text-[#1C1C1C]">Access Denied</h1>
        <p className="text-[#595959] mt-2">Only Super Admins can manage schools.</p>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="schools-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Schools</h1>
          <p className="page-subtitle">Manage schools and subscriptions</p>
        </div>
        <Button onClick={() => setShowAddModal(true)} data-testid="add-school-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add School
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-[#2C4C3B]" />
        </div>
      ) : schools.length === 0 ? (
        <div className="card">
          <div className="card-content text-center py-12">
            <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[#1C1C1C]">No schools yet</h3>
            <p className="text-[#595959] mt-1">Create your first school to get started</p>
            <Button className="mt-4" onClick={() => setShowAddModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add School
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {schools.map((school) => (
            <div key={school.id} className="card" data-testid={`school-card-${school.id}`}>
              <div className="card-content p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#2C4C3B]/10 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-[#2C4C3B]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1C1C1C]">{school.name}</h3>
                      {school.code && (
                        <span className="text-xs text-[#595959] bg-[#F3F4F1] px-2 py-0.5 rounded">
                          {school.code}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(school)}
                      title="Edit school"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(school)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      title="Deactivate school"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Account Type & Status */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    {getTypeBadge(school.account_type)}
                    {getStatusBadge(school.account_status)}
                  </div>
                  {school.plan && (
                    <span className="text-xs text-[#595959]">
                      {PLAN_CONFIG[school.plan]?.label || school.plan}
                    </span>
                  )}
                </div>

                {/* Trial Info */}
                {school.account_type === 'trial' && school.trial_days_remaining !== null && (
                  <div className={`mb-4 p-3 rounded-lg ${school.trial_days_remaining <= 7 ? 'bg-amber-50 border border-amber-200' : 'bg-blue-50 border border-blue-100'}`}>
                    <div className="flex items-center gap-2">
                      <Clock size={14} className={school.trial_days_remaining <= 7 ? 'text-amber-600' : 'text-blue-600'} />
                      <span className={`text-sm font-medium ${school.trial_days_remaining <= 7 ? 'text-amber-700' : 'text-blue-700'}`}>
                        {school.trial_days_remaining} days remaining
                      </span>
                    </div>
                    <p className="text-xs text-[#595959] mt-1">
                      Ends {formatDate(school.trial_ends_at)}
                    </p>
                  </div>
                )}

                {/* Stats */}
                <div className="pt-4 border-t border-[#E5E5E5] grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2 text-sm text-[#595959]">
                    <GraduationCap className="h-4 w-4 text-[#A3A3A3]" />
                    <span>{school.student_count} students</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#595959]">
                    <Users className="h-4 w-4 text-[#A3A3A3]" />
                    <span>{school.user_count} users</span>
                  </div>
                </div>

                {/* Products Enabled */}
                {school.products_enabled && school.products_enabled.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[#E5E5E5]">
                    <div className="flex items-center gap-2 text-xs text-[#595959] mb-2">
                      <Package className="h-3 w-3" />
                      <span>Products:</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {school.products_enabled.map((product) => (
                        <span 
                          key={product} 
                          className="px-2 py-0.5 bg-[#2C4C3B]/10 text-[#2C4C3B] text-xs rounded-full capitalize"
                        >
                          {product}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Manage Button */}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mt-4"
                  onClick={() => handleManageSubscription(school)}
                  data-testid={`manage-subscription-${school.id}`}
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Manage Account
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Security Info Card */}
      <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Shield className="h-5 w-5 text-green-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-green-800">Account Management</h4>
            <p className="text-sm text-green-700 mt-1">
              Schools start as <strong>Pilot</strong> accounts with full access and no billing. 
              Convert to <strong>Trial</strong> when ready to start a countdown, or <strong>Active</strong> for paid subscriptions.
            </p>
          </div>
        </div>
      </div>

      {/* Add/Edit School Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 className="modal-title">{editingSchool ? 'Edit School' : 'Add New School'}</h2>
              <button className="modal-close" onClick={closeModal}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body space-y-4">
                <div>
                  <Label htmlFor="name">School Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter school name"
                    data-testid="school-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="code">School Code (optional)</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="e.g., EPZ, GIA"
                    maxLength={10}
                    data-testid="school-code-input"
                  />
                  <p className="text-xs text-[#595959] mt-1">
                    Short code for quick reference (auto-generated if left blank)
                  </p>
                </div>

                {/* Email Domains Section */}
                <div className="border-t pt-4">
                  <Label className="flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    Approved Email Domains
                  </Label>
                  <p className="text-xs text-[#595959] mt-1 mb-3">
                    Only users with email addresses from these domains can be added to this school.
                  </p>

                  {formData.approved_email_domains.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {formData.approved_email_domains.map((domain) => (
                        <span 
                          key={domain} 
                          className="inline-flex items-center gap-1 px-2 py-1 bg-[#2C4C3B]/10 text-[#2C4C3B] text-sm rounded-full"
                        >
                          @{domain}
                          <button
                            type="button"
                            onClick={() => removeDomain(domain)}
                            className="hover:bg-[#2C4C3B]/20 rounded-full p-0.5"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]">@</span>
                      <Input
                        value={newDomain}
                        onChange={(e) => setNewDomain(e.target.value.toLowerCase())}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addDomain();
                          }
                        }}
                        placeholder="school.edu"
                        className="pl-7"
                        data-testid="domain-input"
                      />
                    </div>
                    <Button type="button" variant="outline" onClick={addDomain}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <Button type="button" variant="outline" onClick={closeModal}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white"
                  disabled={saving}
                  data-testid="save-school-btn"
                >
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {editingSchool ? 'Update School' : 'Create School'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Account Management Modal */}
      {showSubscriptionModal && selectedSchool && (
        <div className="modal-overlay" onClick={closeSubscriptionModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '650px' }}>
            <div className="modal-header">
              <div>
                <h2 className="modal-title">Manage Account</h2>
                <p className="text-sm text-[#595959]">{selectedSchool.name}</p>
              </div>
              <button className="modal-close" onClick={closeSubscriptionModal}>&times;</button>
            </div>
            <form onSubmit={handleAccountSubmit}>
              <div className="modal-body space-y-6">
                {/* Account Type Selection */}
                <div>
                  <Label className="flex items-center gap-2 mb-3">
                    <Package className="h-4 w-4" />
                    Account Type
                  </Label>
                  <div className="grid grid-cols-4 gap-2">
                    {Object.entries(TYPE_CONFIG).map(([key, config]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setAccountData({ ...accountData, account_type: key })}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          accountData.account_type === key 
                            ? 'border-[#2C4C3B] bg-[#2C4C3B]/5' 
                            : 'border-[#E5E5E5] hover:border-[#2C4C3B]/50'
                        }`}
                        data-testid={`type-${key}`}
                      >
                        <span className={`font-medium text-sm ${accountData.account_type === key ? 'text-[#2C4C3B]' : 'text-[#1C1C1C]'}`}>
                          {config.label}
                        </span>
                        <p className="text-xs text-[#595959] mt-1">{config.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Account Status Selection */}
                <div>
                  <Label className="flex items-center gap-2 mb-3">
                    <Settings className="h-4 w-4" />
                    Account Status
                  </Label>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                      const Icon = config.icon;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setAccountData({ ...accountData, account_status: key })}
                          className={`p-3 rounded-lg border text-left transition-all ${
                            accountData.account_status === key 
                              ? 'border-[#2C4C3B] bg-[#2C4C3B]/5' 
                              : 'border-[#E5E5E5] hover:border-[#2C4C3B]/50'
                          }`}
                          data-testid={`status-${key}`}
                        >
                          <div className="flex items-center gap-2">
                            <Icon size={16} className={accountData.account_status === key ? 'text-[#2C4C3B]' : 'text-[#595959]'} />
                            <span className={`font-medium ${accountData.account_status === key ? 'text-[#2C4C3B]' : 'text-[#1C1C1C]'}`}>
                              {config.label}
                            </span>
                          </div>
                          <p className="text-xs text-[#595959] mt-1">{config.description}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Plan Selection (only for trial/paid) */}
                {['trial', 'paid'].includes(accountData.account_type) && (
                  <div>
                    <Label className="flex items-center gap-2 mb-3">
                      <CreditCard className="h-4 w-4" />
                      Plan
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(PLAN_CONFIG).map(([key, config]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setAccountData({ ...accountData, plan: key })}
                          className={`p-3 rounded-lg border text-left transition-all ${
                            accountData.plan === key 
                              ? 'border-[#2C4C3B] bg-[#2C4C3B]/5' 
                              : 'border-[#E5E5E5] hover:border-[#2C4C3B]/50'
                          }`}
                          data-testid={`plan-${key}`}
                        >
                          <span className={`font-medium ${accountData.plan === key ? 'text-[#2C4C3B]' : 'text-[#1C1C1C]'}`}>
                            {config.label}
                          </span>
                          <p className="text-xs text-[#595959] mt-1">{config.description}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Trial Dates (only for trial type) */}
                {accountData.account_type === 'trial' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="trial_start">Trial Start</Label>
                      <Input
                        id="trial_start"
                        type="date"
                        value={accountData.trial_started_at?.split('T')[0] || ''}
                        onChange={(e) => setAccountData({ 
                          ...accountData, 
                          trial_started_at: e.target.value ? new Date(e.target.value).toISOString() : null 
                        })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="trial_end">Trial End</Label>
                      <Input
                        id="trial_end"
                        type="date"
                        value={accountData.trial_ends_at?.split('T')[0] || ''}
                        onChange={(e) => setAccountData({ 
                          ...accountData, 
                          trial_ends_at: e.target.value ? new Date(e.target.value).toISOString() : null 
                        })}
                      />
                    </div>
                  </div>
                )}

                {/* Trial Extension (only if currently trial type) */}
                {selectedSchool.account_type === 'trial' && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <Label className="flex items-center gap-2 mb-2">
                      <Clock className="h-4 w-4 text-amber-600" />
                      Extend Trial
                    </Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Input
                          type="number"
                          min="1"
                          max="60"
                          placeholder="Days to add"
                          value={accountData.extend_trial_days || ''}
                          onChange={(e) => setAccountData({ 
                            ...accountData, 
                            extend_trial_days: e.target.value ? parseInt(e.target.value) : null 
                          })}
                        />
                      </div>
                      <div>
                        <Input
                          placeholder="Reason for extension"
                          value={accountData.extension_reason}
                          onChange={(e) => setAccountData({ 
                            ...accountData, 
                            extension_reason: e.target.value 
                          })}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-amber-700 mt-2">
                      Maximum trial period is 90 days from original start date.
                    </p>
                  </div>
                )}

                {/* Products Enabled */}
                <div>
                  <Label className="flex items-center gap-2 mb-3">
                    <Package className="h-4 w-4" />
                    Products Enabled
                  </Label>
                  <div className="space-y-2">
                    {PRODUCTS.map((product) => (
                      <label
                        key={product.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                          accountData.products_enabled?.includes(product.id)
                            ? 'border-[#2C4C3B] bg-[#2C4C3B]/5'
                            : 'border-[#E5E5E5] hover:border-[#2C4C3B]/50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={accountData.products_enabled?.includes(product.id)}
                          onChange={() => toggleProduct(product.id)}
                          className="sr-only"
                        />
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          accountData.products_enabled?.includes(product.id)
                            ? 'border-[#2C4C3B] bg-[#2C4C3B]'
                            : 'border-[#A3A3A3]'
                        }`}>
                          {accountData.products_enabled?.includes(product.id) && (
                            <CheckCircle2 size={14} className="text-white" />
                          )}
                        </div>
                        <div>
                          <span className="font-medium text-[#1C1C1C]">{product.label}</span>
                          <p className="text-xs text-[#595959]">{product.description}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <textarea
                    id="notes"
                    value={accountData.notes}
                    onChange={(e) => setAccountData({ ...accountData, notes: e.target.value })}
                    placeholder="Internal notes about this account..."
                    className="w-full px-3 py-2 border border-[#E5E5E5] rounded-lg text-sm resize-none"
                    rows={3}
                  />
                </div>

                {/* Last Updated Info */}
                {selectedSchool.account_updated_at && (
                  <div className="text-xs text-[#A3A3A3] pt-2 border-t border-[#E5E5E5]">
                    Last updated: {formatDate(selectedSchool.account_updated_at)}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <Button type="button" variant="outline" onClick={closeSubscriptionModal}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white"
                  disabled={saving}
                  data-testid="save-account-btn"
                >
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Save Changes
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
